/**
 * File animations.js
 * GSAP https://gsap.com/docs/v3/
 */

import {gsap} from 'gsap';
import {ScrollTrigger} from 'gsap/ScrollTrigger';

gsap.registerPlugin(ScrollTrigger);

export function animateSlideInElements() {
    const rectangles = document.querySelectorAll('.rectangle:not(.static-rectangle)');
    if (rectangles) {
        for (const rectangle of rectangles) {
            gsap.matchMedia().add("(max-width: 992px)", () => {
                gsap.from(rectangle, {
                    scrollTrigger: {
                        trigger: rectangle,
                        start: 'top bottom',
                    },
                    x: '-100vw',
                    duration: 1.5,
                });
            }).add("(min-width: 992px)", () => {
                gsap.from(rectangle, {
                    scrollTrigger: {
                        trigger: rectangle,
                        start: 'top bottom',
                        //toggleActions: 'play reset play reset',
                    },
                    x: '-100vw',
                    duration: 1.5,
                });
            });
        }
    }

    const cards = document.querySelectorAll('.contacts-block .card');
    if (cards) {
        for (let [key, card] of Object.entries(cards)) {
            key++;
            gsap.matchMedia().add("(max-width: 992px)", () => {
                gsap.from(card, {
                    scrollTrigger: {
                        trigger: card,
                        start: 'top bottom',
                    },
                    x: key % 2 === 0 ? '100vw' : '-100vw',
                    duration: 1,
                });
            }).add("(min-width: 992px)", () => {
                gsap.from(card, {
                    scrollTrigger: {
                        trigger: card,
                        toggleActions: 'play reset play reset',
                    },
                    x: key % 2 === 0 ? '50vw' : '-50vw',
                    duration: 1,
                });
            });
        }
    }
}

export function navigationSlidingBorder(
    navLinks = document.querySelectorAll('.swiper-pagination .swiper-pagination-bullet'),
    slidingBorder = document.querySelector('.sliding-border'),
    activeLink = document.querySelector('.swiper-pagination .swiper-pagination-bullet-active')
) {

    function setSlidingBorder(link) {
        const linkRect = link.getBoundingClientRect();
        const navRect = link.closest('.swiper-pagination').getBoundingClientRect();

        slidingBorder.style.width = `${linkRect.width}px`;
        slidingBorder.style.left = `${linkRect.left - navRect.left}px`;
    }

    if (activeLink) {
        setSlidingBorder(activeLink);
    }

    navLinks.forEach(link => {
        link.addEventListener('mouseover', (e) => {
            setSlidingBorder(e.target);
        });

        link.addEventListener('mouseout', () => {
            if (activeLink) {
                setSlidingBorder(activeLink);
            } else {
                slidingBorder.style.width = '0';
            }
        });
    });
}

export function numberCounter(numberElement, endNumber = 100, duration = 4) {
    gsap.to(numberElement, {
        scrollTrigger: {
            trigger: numberElement,
        },
        innerText: endNumber,
        duration: duration,
        ease: 'power1.inOut',
        snap: { innerText: 1 },
        onUpdate: function() {
            numberElement.innerText = Math.ceil(numberElement.innerText);
        }
    });
}

export function customScrollbar(
    scrollContainer = document.documentElement,
    customScrollbar =  document.querySelector('.main-scrollbar'),
    useViewPortHeight = true
) {

    const thumb = customScrollbar.querySelector('.bar');
    let isDragging = false;
    let startY;
    let startTop;

    const updateThumbPosition = () => {
        const containerHeight = customScrollbar.clientHeight;
        const contentHeight = scrollContainer.scrollHeight;
        let viewportHeight = window.innerHeight;

        if (scrollContainer.classList.contains('hero-block')) {
            viewportHeight = viewportHeight / 2;
        }

        let scrollRatio;
        if (useViewPortHeight) {
            scrollRatio = viewportHeight / contentHeight;
        } else {
            scrollRatio = containerHeight / contentHeight;
        }

        const thumbHeight = Math.max(containerHeight * scrollRatio, 20);
        thumb.style.height = `${thumbHeight}px`;

        let scrollTop = scrollContainer.scrollTop;
        if (scrollContainer.classList.contains('hero-block')) {
            scrollTop = document.documentElement.scrollTop;
        }

        let thumbTop;
        if (useViewPortHeight) {
            thumbTop = (scrollTop / (contentHeight - viewportHeight)) * (containerHeight - thumbHeight);
        } else {
            thumbTop = (scrollTop / contentHeight) * containerHeight;
        }

        thumb.style.top = `${thumbTop}px`;
    };

    if (useViewPortHeight) {
        window.addEventListener('scroll', updateThumbPosition);
    } else {
        scrollContainer.addEventListener('scroll', updateThumbPosition);
    }
    window.addEventListener('resize', updateThumbPosition);

    thumb.addEventListener('mousedown', (event) => {
        isDragging = true;
        startY = event.clientY;
        startTop = thumb.offsetTop;
        scrollContainer.classList.add('no-select');
    });

    document.addEventListener('mousemove', (event) => {
        if (!isDragging) return;

        const deltaY = event.clientY - startY;
        const newTop = Math.min(Math.max(startTop + deltaY, 0), customScrollbar.clientHeight - thumb.clientHeight);
        thumb.style.top = `${newTop}px`;

        const scrollPercent = newTop / (customScrollbar.clientHeight - thumb.clientHeight);
        scrollContainer.scrollTop = scrollPercent * (scrollContainer.scrollHeight - window.innerHeight);
    });

    document.addEventListener('mouseup', () => {
        isDragging = false;
        scrollContainer.classList.remove('no-select');
    });

    updateThumbPosition();
}

export function toggleTimeline() {
    document.querySelectorAll('.timeline-block').forEach((timeline) => {
        timeline.querySelectorAll('.single-timeline').forEach((single) => {
            single.addEventListener('click', (e) => toggle(e, timeline));
            single.addEventListener('mouseover', (e) => toggle(e, timeline));
        });
    });

    const toggle = (e, timeline) => {
        timeline.querySelectorAll('.single-timeline').forEach((single) => {
            single.classList.remove('active');
        });
        e.currentTarget.classList.add('active');
    }
}

export function toggleCards() {
    document.querySelectorAll('.cards-block, .project-list').forEach((cards) => {
        cards.querySelectorAll('.single-card, .single-project').forEach((single) => {
            single.addEventListener('click', (e) => toggle(e, cards));
            single.addEventListener('mouseover', (e) => toggle(e, cards));
            single.addEventListener('mouseout', (e) => toggle(e, cards, true));
            let faceHeight = 0;
            single.querySelectorAll('[class*="--cube__face"]').forEach((face) => {
                if (face.clientHeight > faceHeight) {
                    faceHeight = face.clientHeight;
                    single.style.setProperty("--height", face.clientHeight+'px');
                }
            });
        });
    });

    const toggle = (e, cards, off = false) => {
        cards.querySelectorAll('.single-card, .single-project').forEach((single) => {
            single.classList.remove('active');
        });
        if (!off) {
            e.currentTarget.classList.add('active');
        }
    }
}


export function blurryTransformedTextFix() {
    const screenWidth = window.innerWidth;
    const scale = 0.00155 * (1920 / screenWidth);
    document.querySelectorAll('.single-card--cube, .single-project--cube').forEach((single) => {
        single.style.setProperty('--scale', 1 + scale);
    });
}

export function initAnimations() {
    animateSlideInElements();
    navigationSlidingBorder();
    toggleTimeline();
    toggleCards();
    blurryTransformedTextFix();

    document.querySelectorAll('.hero-block').forEach((heroBlock) => {
        heroBlock.querySelectorAll('.scroller').forEach((scroll) => {
            customScrollbar(heroBlock, scroll, true);
        });
    });

    document.querySelectorAll('.timeline-block .text-static').forEach((timelineBlockText) => {
        customScrollbar(timelineBlockText.querySelector('.text-static--inner'), timelineBlockText.querySelector('.scrollbar'), false);
    });

    customScrollbar();

    document.querySelectorAll('.statistics-items .statistic-item .number').forEach((number) => {
        const end = number.getAttribute('data-end');
        const duration = number.getAttribute('data-duration');
        number.innerHTML = '0';
        numberCounter(number, end, duration);
    });
}